import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchErrors,
  fetchErrorDetail,
  fetchErrorGroupSpark,
  fetchGuardrails,
  fetchSlowReplies,
  markErrorSeen,
  snoozeErrorGroup,
  type ErrorGroup,
  type GuardrailsResponse,
  type SlowRepliesResponse,
} from '../../api/admin.js';
import { Sparkline } from '../../components/charts/Sparkline.js';
import { MutationModal } from '../../components/ui/MutationModal.js';
import { formatNumber, formatDateTime, shortId } from '../../lib/format.js';
import { GATED_CONTENT_PLACEHOLDER } from '../../lib/privacy.js';

export function ErrorsPage(): ReactElement {
  const [errorKind, setErrorKind] = useState<string>('');
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [selectedSpanId, setSelectedSpanId] = useState<string | null>(null);
  const [snoozeTarget, setSnoozeTarget] = useState<ErrorGroup | null>(null);

  const list = useQuery({
    queryKey: ['admin', 'errors', errorKind],
    queryFn: () => fetchErrors(errorKind ? { errorKind } : {}),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Errors</h1>
          <p className="page-sub">Errores agrupados por (kind, provider, model) · últimos 7 días</p>
        </div>
        <div className="filters">
          <select
            value={errorKind}
            onChange={(e) => setErrorKind(e.target.value)}
            data-testid="error-kind-filter"
          >
            <option value="">Todos los error_kind</option>
            <option value="rate_limit">rate_limit</option>
            <option value="timeout">timeout</option>
            <option value="content_filter">content_filter</option>
            <option value="provider_5xx">provider_5xx</option>
            <option value="malformed">malformed</option>
            <option value="context_overflow">context_overflow</option>
            <option value="auth">auth</option>
            <option value="unknown">unknown</option>
          </select>
        </div>
      </div>
      <SlowRepliesCard />
      <GuardrailsCard />
      <div className="errors-layout">
        <section className="card">
          {list.isPending ? (
            <div className="loading">Cargando…</div>
          ) : list.isError ? (
            <div className="loading">Error: {(list.error).message}</div>
          ) : list.data.groups.length === 0 ? (
            <div className="empty pad-6">Sin errores</div>
          ) : (
            <div className="err-groups">
              {list.data.groups.map((g) => (
                <ErrorGroupItem
                  key={g.groupId}
                  group={g}
                  expanded={expandedGroup === g.groupId}
                  onToggle={() => {
                    setExpandedGroup(expandedGroup === g.groupId ? null : g.groupId);
                  }}
                  onPickSample={(spanId) => setSelectedSpanId(spanId)}
                  onSnooze={() => setSnoozeTarget(g)}
                />
              ))}
            </div>
          )}
        </section>
        {selectedSpanId && <ErrorDetailPane spanId={selectedSpanId} />}
      </div>

      {snoozeTarget ? (
        <SnoozeModal
          group={snoozeTarget}
          onClose={() => setSnoozeTarget(null)}
          onSnoozed={() => {
            setSnoozeTarget(null);
            void list.refetch();
          }}
        />
      ) : null}
    </>
  );
}

function fmtSeconds(ms: number | null): string {
  if (ms === null) return '—';
  return `${(ms / 1000).toFixed(1).replace('.', ',')} s`;
}

/**
 * Cuánto espera el usuario a que el coach empiece a hablar, por modelo.
 *
 * Tercera familia de la misma pregunta que las otras dos tarjetas de esta
 * página ("¿cuánto me está fallando el proveedor?"), y la que faltaba: acá la
 * respuesta llegó y fue buena — sólo que tarde, que para una conversación por
 * voz es su propia forma de estar rota.
 *
 * Mide la COLA, no el promedio: `Reports → model-efficiency` ya da p50/p95 de
 * un rango, y ahí un cuelgue aislado se diluye. Lo que trajo un humano
 * reportando "tardó mucho" es siempre un caso, no una media, así que se cuentan
 * los casos y se muestra el peor con su link a la sesión.
 */
function SlowRepliesCard(): ReactElement | null {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['admin', 'slow-replies'],
    queryFn: () => fetchSlowReplies(),
    refetchInterval: 30_000,
  });

  if (q.isPending) {
    return (
      <section className="card">
        <div className="loading">Cargando…</div>
      </section>
    );
  }
  if (q.isError) {
    return (
      <section className="card">
        <div className="loading">Error: {q.error.message}</div>
      </section>
    );
  }
  const d: SlowRepliesResponse = q.data;
  const totalCalls = d.groups.reduce((a, g) => a + g.calls, 0);
  const totalSlow = d.groups.reduce((a, g) => a + g.slow, 0);
  const totalVerySlow = d.groups.reduce((a, g) => a + g.verySlow, 0);
  const slowLabel = fmtSeconds(d.thresholds.slowMs);

  return (
    <section className="card" data-testid="slow-replies-card">
      <div className="page-header">
        <div>
          <h2 className="page-title">Respuestas lentas</h2>
          <p className="page-sub">
            Cuánto tardó el coach en decir la PRIMERA palabra, por modelo. Últimos 7 días, sin
            sesiones sintéticas.
          </p>
        </div>
      </div>

      <div className="err-events-head tt-tert">
        <strong className="mono">{formatNumber(totalSlow)}</strong> de{' '}
        <span className="mono">{formatNumber(totalCalls)}</span> respuestas tardaron más de{' '}
        {slowLabel} en arrancar
        {totalVerySlow > 0 ? (
          <>
            {' '}
            · <strong className="mono">{formatNumber(totalVerySlow)}</strong> se pasaron de{' '}
            {fmtSeconds(d.thresholds.verySlowMs)} (eso ya es un cuelgue del proveedor, no una
            tardanza)
          </>
        ) : null}
      </div>

      {d.groups.length === 0 ? (
        <div className="empty pad-6">No hubo respuestas de usuarios reales en esta ventana.</div>
      ) : (
        <div className="err-groups">
          {d.groups.map((g) => {
            const id = `${g.provider ?? ''}|${g.model ?? ''}`;
            const open = openGroup === id;
            return (
              <div key={id} className={`err-group ${open ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="err-group-head"
                  onClick={() => setOpenGroup(open ? null : id)}
                  data-testid={`slow-group-${g.model ?? 'sin-modelo'}`}
                >
                  <span className="err-count mono">{formatNumber(g.slow)}</span>
                  {g.verySlow > 0 ? (
                    <span className="badge b-warn">se colgó {g.verySlow}×</span>
                  ) : g.slow > 0 ? (
                    <span className="badge b-warn">lentas</span>
                  ) : (
                    <span className="badge">al día</span>
                  )}
                  <span className="err-meta tt-tert">
                    {g.model ?? '—'} · {formatNumber(g.calls)} respuestas · la mitad arrancó en{' '}
                    {fmtSeconds(g.p50Ms)} · la peor, {fmtSeconds(g.maxMs)}
                  </span>
                  <span className="err-spark">
                    <Sparkline values={g.daily.map((day) => day.slow)} />
                  </span>
                  <span className="err-last tt-tert">
                    {g.worst ? formatDateTime(g.worst.startedAt) : '—'}
                  </span>
                </button>
                {open ? (
                  <div className="err-events">
                    <div className="err-events-head tt-tert">
                      El número grande es cuántas veces el usuario esperó más de {slowLabel} a que
                      el coach empezara a hablar. La rayita muestra cuántas por día.
                    </div>
                    <div className="err-event">
                      Mediana {fmtSeconds(g.p50Ms)} · 95% por debajo de {fmtSeconds(g.p95Ms)} · 99%
                      por debajo de {fmtSeconds(g.p99Ms)} · peor {fmtSeconds(g.maxMs)} ·{' '}
                      {formatNumber(g.measured)} de {formatNumber(g.calls)} con tiempo medido
                      {g.errors > 0 ? (
                        <> · {formatNumber(g.errors)} fallaron (ver el inbox de abajo)</>
                      ) : null}
                    </div>
                    {g.worst ? (
                      <div className="err-event">
                        <span className="tt-tert">{formatDateTime(g.worst.startedAt)}</span> la peor
                        tardó <strong className="mono">{fmtSeconds(g.worst.ttftMs)}</strong> en la
                        primera palabra{' '}
                        {g.worst.sessionId ? (
                          <Link to={`/sessions/${g.worst.sessionId}`}>
                            ver sesión {shortId(g.worst.sessionId)}
                          </Link>
                        ) : null}
                      </div>
                    ) : null}
                    {g.daily.map((day) => (
                      <div key={day.day} className="err-event">
                        <span className="tt-tert">{day.day}</span> · {formatNumber(day.calls)}{' '}
                        respuestas · {formatNumber(day.slow)} por encima de {slowLabel} · peor{' '}
                        {fmtSeconds(day.maxTtftMs)}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * Recuperaciones: el provider arruinó el turno y el backend lo rehizo. NO son
 * errores (el usuario vio una respuesta normal), por eso van en su propia
 * tarjeta y no en el inbox de abajo. Existe porque suprimir sin mostrarlo
 * convertiría una falla del provider en un evento invisible: acá se ve cuánto
 * pasa, con qué modelo, y —el dato que más sirve— QUÉ dijo el modelo.
 */
function GuardrailsCard(): ReactElement {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['admin', 'guardrails'],
    queryFn: () => fetchGuardrails(),
    // Se mira mientras se prueba a mano: conviene que se refresque solo.
    refetchInterval: 30_000,
  });

  if (q.isPending) return <section className="card"><div className="loading">Cargando…</div></section>;
  if (q.isError) {
    return (
      <section className="card">
        <div className="loading">Error: {q.error.message}</div>
      </section>
    );
  }
  const d: GuardrailsResponse = q.data;
  const totalGuard = d.replyGuard.reduce((a, g) => a + g.count, 0);
  const totalThought = d.thoughtDrops.reduce((a, g) => a + g.calls, 0);
  const pct = d.replyCalls > 0 ? ((totalGuard / d.replyCalls) * 100).toFixed(1) : '0.0';

  return (
    <section className="card" data-testid="guardrails-card">
      <div className="page-header">
        <div>
          <h2 className="page-title">Turnos recuperados</h2>
          <p className="page-sub">
            El modelo devolvió algo inservible y el backend lo rehizo — el usuario vio una
            respuesta normal. Últimos 7 días, sin sesiones sintéticas.
          </p>
        </div>
      </div>

      <div className="err-events-head tt-tert">
        <strong className="mono">{formatNumber(totalGuard)}</strong> de{' '}
        <span className="mono">{formatNumber(d.replyCalls)}</span> respuestas ({pct}%) hubo que
        rehacerlas · <strong className="mono">{formatNumber(totalThought)}</strong> llamadas
        trajeron pensamiento etiquetado (filtrado, sin rehacer nada)
      </div>

      {totalGuard === 0 && totalThought === 0 ? (
        <div className="empty pad-6">
          Todavía no pasó ninguna vez en esta ventana.
        </div>
      ) : (
        <div className="err-groups">
          {d.replyGuard.map((g) => {
            const id = `${g.provider ?? ''}|${g.model ?? ''}|${g.reason ?? ''}|${g.outcome ?? ''}`;
            const open = openGroup === id;
            return (
              <div key={id} className={`err-group ${open ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="err-group-head"
                  onClick={() => setOpenGroup(open ? null : id)}
                >
                  <span className="err-count mono">{formatNumber(g.count)}</span>
                  <span className="badge b-warn">
                    {g.reason === 'degenerate_opening' ? 'arrancó rara' : 'vino vacía'}
                  </span>
                  <span className="err-meta tt-tert">
                    {g.provider ?? '—'} · {g.model ?? '—'} ·{' '}
                    {g.outcome === 'fallback'
                      ? 'falló las 2 veces (el usuario oyó la disculpa)'
                      : 'se recuperó al reintentar'}
                  </span>
                  <span className="err-last tt-tert">{formatDateTime(g.lastSeen)}</span>
                </button>
                {open ? (
                  <div className="err-events">
                    <div className="err-events-head tt-tert">
                      Lo que el modelo dijo y no se mostró ({g.samples.length} más recientes)
                    </div>
                    {g.samples.map((s) => (
                      <div key={s.spanId} className="err-event">
                        <span className="tt-tert">{formatDateTime(s.startedAt)}</span>{' '}
                        <code className="mono">
                          {s.suppressed === null
                            ? GATED_CONTENT_PLACEHOLDER
                            : JSON.stringify(s.suppressed)}
                        </code>{' '}
                        {s.sessionId ? (
                          <Link to={`/sessions/${s.sessionId}`}>
                            ver sesión {shortId(s.sessionId)}
                          </Link>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
          {d.thoughtDrops.map((t) => (
            <div key={`th-${t.model ?? ''}`} className="err-group">
              <div className="err-group-head">
                <span className="err-count mono">{formatNumber(t.calls)}</span>
                <span className="badge b-warn">pensamiento etiquetado</span>
                <span className="err-meta tt-tert">
                  {t.model ?? '—'} · {formatNumber(t.parts)} pedazos ·{' '}
                  {formatNumber(t.chars)} caracteres descartados
                </span>
                <span className="err-last tt-tert">{formatDateTime(t.lastSeen)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ErrorGroupItem({
  group,
  expanded,
  onToggle,
  onPickSample,
  onSnooze,
}: {
  group: ErrorGroup;
  expanded: boolean;
  onToggle: () => void;
  onPickSample: (spanId: string) => void;
  onSnooze: () => void;
}): ReactElement {
  const spark = useQuery({
    queryKey: ['admin', 'errors', 'spark', group.groupId],
    queryFn: () => fetchErrorGroupSpark(group.groupId),
    enabled: expanded,
  });

  return (
    <div className={`err-group ${expanded ? 'is-open' : ''}`}>
      <button
        type="button"
        className="err-group-head"
        onClick={onToggle}
        data-testid={`err-group-${group.groupId}`}
      >
        <span className="err-count mono">{formatNumber(group.count)}</span>
        <span className="badge b-warn">{group.errorKind ?? '(sin kind)'}</span>
        <span className="err-meta tt-tert">
          {group.provider ?? '—'} · {group.model ?? '—'} ·{' '}
          <span className={`kind-pill kind-${group.kind}`}>{group.kind}</span>
        </span>
        <span className="err-spark">
          {expanded && spark.data ? <Sparkline values={spark.data.buckets} /> : null}
        </span>
        <span className="err-last tt-tert">{formatDateTime(group.lastSeen)}</span>
        <span
          className="btn"
          onClick={(e) => {
            e.stopPropagation();
            onSnooze();
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              onSnooze();
            }
          }}
          data-testid={`snooze-${group.groupId}`}
        >
          Snooze
        </span>
      </button>
      {expanded ? (
        <div className="err-events">
          <div className="err-events-head tt-tert">
            Sample span ids ({group.sampleSpanIds.length})
          </div>
          {group.sampleSpanIds.map((id) => (
            <button
              type="button"
              key={id}
              className="err-event link-btn"
              onClick={() => onPickSample(id)}
            >
              {shortId(id)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SnoozeModal({
  group,
  onClose,
  onSnoozed,
}: {
  group: ErrorGroup;
  onClose: () => void;
  onSnoozed: () => void;
}): ReactElement {
  const mut = useMutation({
    mutationFn: (reason: string) => snoozeErrorGroup(group.groupId, 60, reason || undefined),
    onSuccess: onSnoozed,
  });
  return (
    <MutationModal
      open={true}
      title="Silenciar grupo de errores"
      description={
        <>
          <strong>{group.errorKind ?? '(sin kind)'}</strong> · {group.provider ?? '—'} ·{' '}
          {group.model ?? '—'} ({group.kind}) — TTL 60 minutos.
        </>
      }
      confirmLabel="Snooze 60 min"
      requiresReason={false}
      tone="warn"
      isPending={mut.isPending}
      errorMessage={mut.error instanceof Error ? mut.error.message : null}
      onCancel={onClose}
      onConfirm={(reason) => mut.mutate(reason)}
    />
  );
}

function ErrorDetailPane({ spanId }: { spanId: string }): ReactElement {
  const detail = useQuery({
    queryKey: ['admin', 'errors', 'detail', spanId],
    queryFn: () => fetchErrorDetail(spanId),
  });
  const qc = useQueryClient();
  const seenMut = useMutation({
    mutationFn: () => markErrorSeen(spanId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'errors', 'detail', spanId] });
    },
  });
  return (
    <aside className="card detail-pane">
      <header className="card-head">
        <h3 className="card-title">Span detail</h3>
      </header>
      {detail.isPending ? (
        <div className="loading">Cargando…</div>
      ) : detail.isError ? (
        <div className="loading">Error: {(detail.error).message}</div>
      ) : (
        <dl className="kv">
          <dt>id</dt>
          <dd>
            <code>{detail.data.id}</code>
          </dd>
          <dt>trace</dt>
          <dd>
            <code>{detail.data.traceId}</code>
          </dd>
          <dt>kind / name</dt>
          <dd>
            {detail.data.kind} · {detail.data.name}
          </dd>
          <dt>error_kind</dt>
          <dd>{detail.data.errorKind ?? '—'}</dd>
          <dt>duration</dt>
          <dd>{detail.data.durationMs}ms</dd>
          <dt>started</dt>
          <dd>{formatDateTime(detail.data.startedAt)}</dd>
          <dt>seen</dt>
          <dd>
            {detail.data.seen
              ? `por ${shortId(detail.data.seen.byAdminId)} · ${formatDateTime(detail.data.seen.atIso)}`
              : '—'}
          </dd>
          <dt>error_payload</dt>
          <dd>
            {detail.data.gated ? (
              <span className="t-meta">{GATED_CONTENT_PLACEHOLDER}</span>
            ) : (
              <pre className="code-block">
                {JSON.stringify(detail.data.errorPayload, null, 2)}
              </pre>
            )}
          </dd>
          <dt>attributes</dt>
          <dd>
            <pre className="code-block">
              {JSON.stringify(detail.data.attributes, null, 2)}
            </pre>
          </dd>
        </dl>
      )}
      <footer className="detail-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => seenMut.mutate()}
          disabled={seenMut.isPending}
          data-testid="mark-seen"
        >
          {seenMut.isPending ? 'Marcando…' : 'Marcar como visto'}
        </button>
      </footer>
    </aside>
  );
}
